// The avatars (docs/plans/2026-10-19-avatars.md): 32 faces a player can wear, left to right and top to bottom on the sheet
// (art/avatars/sheet.png, sliced by tools/make-avatars.py into public/avatars/NN.png, NN = id + 1). A face has a name, and
// the name is what other players see. Every name here is invented: a role, a corner of a city, or an ordinary first name
// and surname. None is, or puns on, a real person; keep it that way (the copy lint and tests/avatars.test.ts hold the list).
//
// `held` faces wear a head covering or a mark the red lines send for review (docs/plans/2026-10-19-avatars.md section 4).
// They keep their ids so the sheet stays in order, but nobody can be dealt or pick one until they are cleared.
//
// `side` is the roster a face belongs to (docs/plans/2026-10-19-character-select.md section 2): until the police art
// arrives, sheet rows 1 and 2 (01-16) march and rows 3 and 4 (17-32) hold the line. It is data: when the police faces
// come they replace the C rows here, and these 16 move to P.
export type Side = 'P' | 'C';
export interface Avatar { name: string; side: Side; held?: true }

export const AVATARS: Avatar[] = [
  { name: 'Navneet Tipke', side: 'P' }, { name: 'Metro Didi', side: 'P' }, { name: 'Santosh Danka', side: 'P' }, { name: 'Library Didi', side: 'P' },
  { name: 'Tikka Tau', side: 'P' }, { name: 'Sneha Vora', side: 'P' }, { name: 'Gaurav Vyas', side: 'P' }, { name: 'WhatsApp Mausi', side: 'P' },
  { name: 'Notes Bhaiya', side: 'P' }, { name: 'Mela Meena', side: 'P', held: true }, { name: 'Bike Bhaiya', side: 'P' }, { name: 'Canteen Didi', side: 'P' },
  { name: 'Forward Chacha', side: 'P' }, { name: 'Chowk Chhaya', side: 'P', held: true }, { name: 'Backbench Bunty', side: 'P' }, { name: 'Hostel Didi', side: 'P' },
  { name: 'Reel Rani', side: 'C' }, { name: 'Daftar Dev', side: 'C', held: true }, { name: 'Pickle Aunty', side: 'C' }, { name: 'Gym Bhai', side: 'C' },
  { name: 'Chai Rani', side: 'C' }, { name: 'Sanjay', side: 'C' }, { name: 'Bazaar Bela', side: 'C', held: true }, { name: 'Pension Uncle', side: 'C' },
  { name: 'Open-Mic Omi', side: 'C' }, { name: 'Pani-Puri Pinky', side: 'C' }, { name: 'Auto Bhaiya', side: 'C' }, { name: 'Tuition Madam', side: 'C' },
  { name: 'Sticker Sonu', side: 'C' }, { name: 'Bunk Bittu', side: 'C' }, { name: 'Night-Shift Neha', side: 'C' }, { name: 'Retired Sir', side: 'C' },
];

export const AVATAR_COUNT = AVATARS.length;

/** Whether v is the id of a face a player can wear: an integer in range that is not held for review. */
export const isAvatar = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0 && v < AVATAR_COUNT && !AVATARS[v].held;

/** The ids a player can pick or be dealt. */
export const pickable = (): number[] => AVATARS.flatMap((a, i) => (a.held ? [] : [i]));

/** A side's roster: the ids of its faces a player can pick, in sheet order (held faces stay out). */
export const roster = (side: Side): number[] => pickable().filter((i) => AVATARS[i].side === side);

/** The name that goes with a face, or '' for no face (-1). */
export const avatarName = (av: number): string => AVATARS[av]?.name ?? '';

/** Where the face is served: /avatars/01.png for id 0. */
export const avatarSrc = (av: number): string => `/avatars/${String(av + 1).padStart(2, '0')}.png`;

/**
 * The face a joiner gets: the one they asked for if it is a real, pickable face nobody in the room wears; else the next
 * pickable face after it that is free (so two players never share one); else, with every face taken, the one they asked for.
 * -1 when they asked for none, which is how an old client or a bot joins.
 */
export function avatarFor(want: unknown, taken: Iterable<number>): number {
  if (!isAvatar(want)) return -1;
  const used = new Set(taken), ids = pickable(), at = ids.indexOf(want);
  for (let k = 0; k < ids.length; k++) { const id = ids[(at + k) % ids.length]; if (!used.has(id)) return id; }
  return want;
}
